import { ADDIN_NAMESPACE } from '../config/addinNamespace';
import { parseBifrostFormula, type ParsedBifrostFormula } from '../taskpane/formulaParsing';

/**
 * Bifrost Entries — drill from the cell holding the number.
 *
 * The point of Entries is answering "what is this made of" about a specific
 * figure a user is looking at. Retyping the formula name and every parameter
 * into a second call is exactly the friction that made the drilldown variant
 * go unused, so this reads the selected cell and writes the _ENTRIES call for
 * it, arguments intact.
 *
 * It writes a formula rather than pasting values: the spilled range then stays
 * correct when the underlying data changes, and it reconciles to the cell it
 * came from because it is the same query.
 */

/** One drilldown function for every formula, whatever it queries. */
const ENTRIES_FUNCTION = 'ENTRIES';

export interface SelectedFormula {
  /** The cell's own formula text, as Excel reports it. */
  formulaText: string;
  /** A1-style address of the selected cell, e.g. "Sheet1!B4". */
  address: string;
  parsed: ParsedBifrostFormula;
  /** The call this drilldown would write. */
  entriesFormula: string;
}

/**
 * Re-emit a parameter exactly as it was written.
 *
 * Quoted-ness comes from the parse, never from inspecting the value: a customer
 * code like "C1" is indistinguishable from a reference to cell C1 once the
 * quotes are gone, and guessing turns one into the other. Anything unquoted —
 * a reference, a range, a nested expression — is passed through untouched.
 */
function renderParam(value: string | number, wasQuoted: boolean): string {
  if (!wasQuoted) return String(value);
  return `"${String(value).replace(/"/g, '""')}"`;
}

export function buildEntriesFormula(parsed: ParsedBifrostFormula): string {
  const args = [
    // The formula name is always a quoted string literal.
    renderParam(parsed.formulaName, true),
    ...parsed.params.map((value, i) => renderParam(value, parsed.paramsQuoted[i] ?? true)),
  ];
  return `=${ADDIN_NAMESPACE}.${ENTRIES_FUNCTION}(${args.join(',')})`;
}

/**
 * Inspect the current selection. Returns null when it is not a single cell
 * carrying one of this build's formulas — the pane says so rather than
 * offering a drilldown that would produce nothing.
 */
export async function readSelectedFormula(): Promise<SelectedFormula | null> {
  return Excel.run(async context => {
    const range = context.workbook.getSelectedRange();
    range.load(['formulas', 'address', 'rowCount', 'columnCount']);
    await context.sync();

    if (range.rowCount !== 1 || range.columnCount !== 1) return null;

    const formulaText = String(range.formulas?.[0]?.[0] ?? '');
    const parsed = parseBifrostFormula(formulaText);
    if (!parsed) return null;

    return {
      formulaText,
      address: range.address,
      parsed,
      entriesFormula: buildEntriesFormula(parsed),
    };
  });
}

export type InsertTarget = 'below' | 'right' | 'newSheet';

/**
 * Write the Entries call so its spill has somewhere to go.
 *
 * Two rows below rather than one: the spilled range starts with a header row,
 * and landing it directly under the value made the header read as a label for
 * the number above it.
 */
export async function insertEntriesFormula(
  selected: SelectedFormula,
  target: InsertTarget
): Promise<{ sheetName: string; address: string }> {
  return Excel.run(async context => {
    const source = context.workbook.getSelectedRange();
    source.load(['rowIndex', 'columnIndex']);
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await context.sync();

    if (target === 'newSheet') {
      // Named for the formula, deduplicated by suffix: drilling the same
      // formula twice should not fail on a name collision.
      const base = `Entries ${selected.parsed.formulaName}`.slice(0, 28);
      const sheets = context.workbook.worksheets;
      sheets.load('items/name');
      await context.sync();

      const taken = new Set(sheets.items.map(s => s.name));
      let name = base;
      for (let i = 2; taken.has(name); i++) name = `${base.slice(0, 25)} ${i}`;

      const created = sheets.add(name);
      const cell = created.getRange('A1');
      cell.formulas = [[selected.entriesFormula]];
      created.activate();
      await context.sync();
      return { sheetName: name, address: 'A1' };
    }

    const rowOffset = target === 'below' ? 2 : 0;
    const colOffset = target === 'right' ? 2 : 0;
    const destination = sheet.getCell(source.rowIndex + rowOffset, source.columnIndex + colOffset);
    destination.formulas = [[selected.entriesFormula]];
    destination.load('address');
    await context.sync();

    return { sheetName: sheet.name, address: destination.address };
  });
}
